import { useAtom, useSetAtom } from 'jotai'
import { useEffect } from 'react'
import { useLifecyclePersistence, useSyncLifecycleToExistingAtom } from '../../utilities'
import { ConformedCampaignDetails } from '../../views'
import { campaignDetailsAtom, campaignDetailsStatusAtom, useCampaignDetailsListener } from '../state'
import { campaignDetailsGenerationErrorAtom, campaignDetailsSavingAtom } from '../state/detail-atoms'

export type UseCampaignDetailsArguments = {
	details: ConformedCampaignDetails | undefined
	isPending: boolean
	isSaving: boolean
	isGenerating: boolean
	onDetailsChange: (details: ConformedCampaignDetails) => void
	detailsVersion?: number | string | Date | null
	generationError?: string | null
}

export function useCampaignDetails({
	details,
	isPending,
	isSaving,
	isGenerating,
	onDetailsChange,
	detailsVersion,
	generationError,
}: UseCampaignDetailsArguments) {
	const [campaignDetails, setCampaignDetails] = useAtom(campaignDetailsAtom)
	const setGenerationError = useSetAtom(campaignDetailsGenerationErrorAtom)

	useEffect(() => {
		setGenerationError(generationError ?? null)
	}, [generationError, setGenerationError])

	// New lifecycle + persistence management
	const detailsLifecycle = useLifecyclePersistence<ConformedCampaignDetails>({
		data: details ?? undefined,
		isIdle: (d) => d == undefined,
		isPending: () => isPending,
		isGenerating: () => isGenerating,
		save: async (campaignDetails) => {
			await onDetailsChange(campaignDetails)
		},
		debounceMs: 500,
		getVersion: () => detailsVersion ?? null,
		debug: false,
		name: 'useCampaignDetails',
	})

	// Direct wiring: use the existing campaignDetailsAtom and its listener
	useSyncLifecycleToExistingAtom(
		detailsLifecycle,
		{
			dataAtom: campaignDetailsAtom,
			useDataListener: useCampaignDetailsListener,
			// TODO: Add lifecycle and persistence atoms when available
			lifecycleAtom: campaignDetailsStatusAtom,
			persistenceAtom: campaignDetailsSavingAtom,
		},
	)

	return {}
}
